package com.advaita.cloud.drive.shared

/**
 * Delimiters the tabular preview can use. [label] is what the override control
 * shows the user.
 */
enum class Delimiter(val char: Char, val label: String) {
    COMMA(',', "Comma"),
    TAB('\t', "Tab"),
    SEMICOLON(';', "Semicolon"),
    PIPE('|', "Pipe"),
}

/**
 * A parsed grid, already bounded to what the renderer will draw.
 *
 * [header] is null when the first record is data rather than column names.
 * Every row is padded to [columnCount] so the grid cannot lie about which
 * column a value is in.
 *
 * The clipped flags exist so the UI can SAY what it is hiding. A preview that
 * silently drops rows or columns reads as a complete file, which is the failure
 * ADV-1202 calls out.
 *
 * CAUTION on the totals: [totalRowCount] counts the records in the text that
 * was PARSED. When the bytes were truncated at the ceiling that is the prefix's
 * row count, not the file's, so a caller must not present it as "of the whole
 * file" — see [rowsClipped]'s use in the renderer.
 */
data class DelimitedTable(
    val header: List<String>?,
    val rows: List<List<String>>,
    val columnCount: Int,
    val totalRowCount: Int,
    val totalColumnCount: Int,
) {
    val rowsClipped: Boolean get() = totalRowCount > rows.size
    val columnsClipped: Boolean get() = totalColumnCount > columnCount
}

/**
 * RFC 4180 parsing for the ADV-1202 tabular preview.
 *
 * Lives in `shared/commonMain` rather than in the frontend on purpose: the
 * frontend has no test source set, and this is the only logic-dense part of the
 * tabular preview. Here it compiles to JS for the renderer and to JVM for
 * `DelimitedTextParserTest`.
 *
 * `split(delimiter)` is not good enough. A quoted field may contain the
 * delimiter, a newline, or an escaped quote, and real files have all three.
 *
 * A quote only OPENS a quoted field at the start of a field. A `"` anywhere
 * else is a literal character. An earlier version toggled quote state on every
 * `"`, which meant one inch mark (`5" region`) flipped the parser into quoted
 * mode for the rest of the document and silently swallowed every following row.
 * Excel, LibreOffice and Python's `csv` all treat a mid-field quote as literal;
 * so does this.
 */
object DelimitedTextParser {

    /** Records sampled when guessing the delimiter. Enough to see a pattern. */
    private const val DETECT_SAMPLE_RECORDS = 10

    /**
     * A byte-order mark. Excel writes one on CSV export, and left in place it
     * becomes part of the first column's heading.
     */
    private const val BOM = '﻿'

    /**
     * Guess the delimiter from the content, falling back to the file name.
     *
     * Content wins over the extension because a tab-delimited file named `.csv`
     * is common enough to matter. Scoring prefers the candidate that yields a
     * CONSISTENT column count across the sample — a delimiter that happens to
     * appear in one row scores worse than one structuring every row.
     */
    fun detectDelimiter(text: String, fileName: String): Delimiter {
        val clean = text.removePrefix(BOM.toString())
        val fromName = fromFileName(fileName)
        if (clean.isEmpty()) return fromName

        var best: Delimiter? = null
        var bestScore = 0
        var bestFields = 0
        for (candidate in Delimiter.entries) {
            // Each candidate gets its own scan: field boundaries depend on the
            // delimiter, and so therefore does where a quote may open.
            val sample = scan(clean, candidate.char).records
                .take(DETECT_SAMPLE_RECORDS)
                .filterNot { it.size == 1 && it[0].isEmpty() }
            if (sample.isEmpty()) continue
            val counts = sample.map { it.size }
            val modalFields = counts.groupingBy { it }.eachCount().maxByOrNull { it.value }?.key ?: 1
            // A single field means this character never separated anything.
            if (modalFields < 2) continue
            val agreeing = counts.count { it == modalFields }
            val isBetter = agreeing > bestScore ||
                (agreeing == bestScore && modalFields > bestFields) ||
                (agreeing == bestScore && modalFields == bestFields && candidate == fromName)
            if (isBetter) {
                best = candidate
                bestScore = agreeing
                bestFields = modalFields
            }
        }
        return best ?: fromName
    }

    /**
     * Parse [text] into a grid.
     *
     * @param dropPartialLastRecord set when the content was truncated. A byte
     *   cut lands mid-line, so the final record is half a row and would render
     *   wrong values under the wrong headings. Fires only when the text did NOT
     *   end on a record boundary. A sole record is kept even then: one partial
     *   row beats an empty pane, and nothing better exists.
     * @param maxRows bound on BODY rows (the header is extra). This is how the
     *   preview stays cheap instead of virtualizing: the renderer draws every
     *   row it is handed, so the bound belongs here.
     * @param maxColumns bound on columns. A wide matrix does not need every
     *   column on screen to be a useful peek.
     */
    fun parse(
        text: String,
        delimiter: Delimiter,
        firstRowIsHeader: Boolean,
        dropPartialLastRecord: Boolean = false,
        maxRows: Int = Int.MAX_VALUE,
        maxColumns: Int = Int.MAX_VALUE,
    ): DelimitedTable {
        val scanned = scan(text.removePrefix(BOM.toString()), delimiter.char)
        var records = scanned.records

        // Trailing blank lines are terminators, not rows. A file ending "\n\n"
        // (routine from shell pipelines and Excel) produced a phantom empty row
        // when only ONE was dropped, so drop them all.
        while (records.isNotEmpty() && isBlank(records.last())) {
            records = records.dropLast(1)
        }
        if (dropPartialLastRecord && !scanned.endedOnBoundary && records.size > 1) {
            records = records.dropLast(1)
        }
        if (records.isEmpty()) return DelimitedTable(null, emptyList(), 0, 0, 0)

        val header = if (firstRowIsHeader) records.first() else null
        val body = if (firstRowIsHeader) records.drop(1) else records

        // Widths are measured across every parsed record, then clipped, so the
        // reported total is not the truth-after-clipping.
        val totalColumnCount = maxOf(header?.size ?: 0, body.maxOfOrNull { it.size } ?: 0)
        val columnCount = minOf(totalColumnCount, maxColumns)

        return DelimitedTable(
            header = header?.let { fit(it, columnCount) },
            rows = body.take(maxRows).map { fit(it, columnCount) },
            columnCount = columnCount,
            totalRowCount = body.size,
            totalColumnCount = totalColumnCount,
        )
    }

    private fun isBlank(record: List<String>): Boolean =
        record.size == 1 && record[0].isEmpty()

    /** Pad a short record and clip a long one, so every row is exactly [width]. */
    private fun fit(row: List<String>, width: Int): List<String> = when {
        row.size == width -> row
        row.size > width -> row.take(width)
        else -> row + List(width - row.size) { "" }
    }

    private fun fromFileName(fileName: String): Delimiter =
        // Only .tsv is claimed: `.txt` routes to the TEXT renderer and never
        // reaches here, so mapping it would be an unreachable wrong answer.
        if (fileName.substringAfterLast('.', "").lowercase() == "tsv") Delimiter.TAB else Delimiter.COMMA

    /** [endedOnBoundary] is true when the text finished with a record break. */
    private data class Scan(val records: List<List<String>>, val endedOnBoundary: Boolean)

    /**
     * One pass: split into records and fields together, because a quote may only
     * open a field and "where a field starts" depends on the delimiter.
     */
    private fun scan(text: String, delimiter: Char): Scan {
        if (text.isEmpty()) return Scan(emptyList(), endedOnBoundary = true)

        val records = mutableListOf<List<String>>()
        var fields = mutableListOf<String>()
        val field = StringBuilder()
        var inQuotes = false
        var atFieldStart = true
        var endedOnBoundary = false
        var i = 0

        while (i < text.length) {
            val c = text[i]
            when {
                inQuotes && c == '"' -> {
                    // A doubled quote inside a quoted field is one literal quote.
                    if (i + 1 < text.length && text[i + 1] == '"') {
                        field.append('"')
                        i++
                    } else {
                        inQuotes = false
                    }
                    endedOnBoundary = false
                }
                // The ONLY place a quoted field can begin.
                !inQuotes && c == '"' && atFieldStart -> {
                    inQuotes = true
                    atFieldStart = false
                    endedOnBoundary = false
                }
                !inQuotes && c == delimiter -> {
                    fields.add(field.toString())
                    field.clear()
                    atFieldStart = true
                    endedOnBoundary = false
                }
                !inQuotes && (c == '\n' || c == '\r') -> {
                    fields.add(field.toString())
                    field.clear()
                    records.add(fields)
                    fields = mutableListOf()
                    atFieldStart = true
                    endedOnBoundary = true
                    // Consume the LF of a CRLF pair so it is one break, not two.
                    if (c == '\r' && i + 1 < text.length && text[i + 1] == '\n') i++
                }
                else -> {
                    field.append(c)
                    atFieldStart = false
                    endedOnBoundary = false
                }
            }
            i++
        }
        // An unterminated final record (no trailing break) still counts.
        if (!endedOnBoundary) {
            fields.add(field.toString())
            records.add(fields)
        }
        return Scan(records, endedOnBoundary)
    }
}
